import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import LockedPhotoCard from "@/components/LockedPhotoCard";
import UnlockPaymentModal from "@/components/UnlockPaymentModal";
import SafetyActions from "@/components/SafetyActions";
import { ArrowLeft, Send, ImagePlus, HelpCircle, Star, Check, Languages, ExternalLink } from "lucide-react";
import { initials } from "@/lib/format";
import { euro } from "@/lib/format";
import { toast } from "sonner";
import { prepareExternal, openExternal, cancelExternal } from "@/lib/external";

const Conversation = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t, lang } = useI18n();
  const [friend, setFriend] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqNote, setReqNote] = useState("");
  const [creator, setCreator] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [translations, setTranslations] = useState({}); // {msgId: {text, shown}}
  const [translating, setTranslating] = useState(null);
  const bottomRef = useRef();

  const translateMsg = async (msg) => {
    const existing = translations[msg.id];
    if (existing) {
      setTranslations((p) => ({ ...p, [msg.id]: { ...existing, shown: !existing.shown } }));
      return;
    }
    setTranslating(msg.id);
    try {
      const { data } = await api.post("/translate", { text: msg.text, target_lang: lang });
      setTranslations((p) => ({ ...p, [msg.id]: { text: data.translated_text, shown: true } }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTranslating(null);
    }
  };


  const loadCreator = useCallback(async () => {
    try {
      const { data } = await api.get(`/creators/${encodeURIComponent(username)}`);
      setCreator(data);
    } catch (e) {}
  }, [username]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/conversations/${encodeURIComponent(username)}`);
      setFriend(data.friend);
      setItems(data.items);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/messages");
    } finally {
      setLoading(false);
    }
  }, [username, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCreator(); }, [loadCreator]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items]);

  const subscribe = async (plan) => {
    const reserved = prepareExternal();
    try {
      const { data } = await api.post(
        `/creators/${encodeURIComponent(username)}/subscribe?plan=${plan}`,
        { origin_url: window.location.origin }
      );
      if (openExternal(data.checkout_url, reserved) === "tab") toast.info("Le paiement Stripe s'est ouvert dans un nouvel onglet.");
    } catch (e) {
      cancelExternal(reserved);
      toast.error(apiError(e));
    }
  };

  const sendMsg = async () => {
    if (!text.trim()) return;
    const t = text.trim();
    setText("");
    try {
      await api.post("/messages", { receiver_username: username, text: t });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendRequest = async () => {
    try {
      await api.post("/photo-requests", { receiver_username: username, note: reqNote });
      toast.success("Demande de photo envoyée");
      setReqOpen(false);
      setReqNote("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openUnlock = (photo) => { setSelected(photo); setModalOpen(true); api.post(`/photos/${photo.id}/view`).catch(() => {}); };

  const confirmUnlock = async (photo) => {
    try {
      const { data } = await api.post(`/photos/${photo.id}/purchase`);
      setUser(data.user);
      toast.success("Photo déverrouillée");
      await load();
    } catch (e) {
      toast.error(apiError(e));
      throw e;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)]">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3 border-b border-border">
        <button onClick={() => navigate("/messages")} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-secondary" data-testid="conversation-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Avatar className="h-9 w-9">
          {friend?.avatar && <AvatarImage src={friend.avatar} alt={friend.username} />}
          <AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(friend?.username || username)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0"><p className="font-medium truncate">@{friend?.username || username}</p></div>
        <button
          onClick={() => navigate(`/c/${username}`)}
          className="h-9 w-9 grid place-items-center rounded-xl hover:bg-secondary transition-colors"
          data-testid="conversation-view-vitrine"
          aria-label={t("wall.creatorPage")}
        >
          <ExternalLink className="h-5 w-5" />
        </button>
        {creator?.sub_enabled && (creator.is_subscribed ? (
          <Badge className="bg-[hsla(214,100%,62%,0.15)] text-primary border-0 gap-1"><Check className="h-3 w-3" /> {t("common.subscribed")}</Badge>
        ) : (
          <Button size="sm" className="rounded-xl neon-glow-blue-sm" onClick={() => setSubOpen(true)} data-testid="subscribe-button">
            <Star className="h-4 w-4 mr-1" /> {t("common.subscribe")}
          </Button>
        ))}
        {friend && <SafetyActions targetUser={friend} onBlocked={() => navigate("/messages")} />}
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="conversation-thread">
        {loading ? (
          <p className="text-center text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-10">{t("conv.start")}</p>
        ) : (
          items.map((it) => {
            if (it.kind === "message") {
              const tr = translations[it.id];
              const showTr = tr?.shown;
              return (
                <div key={it.id} className={`flex flex-col ${it.is_mine ? "items-end" : "items-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${it.is_mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    {showTr ? tr.text : it.text}
                  </div>
                  <button
                    onClick={() => translateMsg(it)}
                    disabled={translating === it.id}
                    className="mt-1 px-1 flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`translate-message-${it.id}`}
                  >
                    <Languages className="h-3 w-3" />
                    {translating === it.id
                      ? t("conv.translating")
                      : showTr
                      ? t("common.showOriginal")
                      : t("common.translate")}
                  </button>
                </div>
              );
            }
            if (it.kind === "request") {
              const mine = it.is_mine;
              return (
                <div key={it.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="photo-request-item">
                  <div className={`max-w-[82%] rounded-2xl px-3.5 py-3 text-sm border ${mine ? "bg-primary/10 border-primary/30" : "bg-secondary border-border"}`}>
                    <div className="flex items-center gap-2 font-medium">
                      <HelpCircle className="h-4 w-4 text-primary" />
                      {mine ? "Vous avez demandé une photo" : `@${friend?.username} vous demande une photo`}
                    </div>
                    {it.note && <p className="text-muted-foreground mt-1 italic">« {it.note} »</p>}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {it.status === "fulfilled" ? (
                        <Badge className="bg-[hsla(152,72%,45%,0.18)] text-[hsl(152_72%_60%)] border-0">Photo envoyée</Badge>
                      ) : (
                        <Badge variant="secondary">En attente</Badge>
                      )}
                      {!mine && it.status !== "fulfilled" && (
                        <Button size="sm" className="rounded-xl neon-glow-blue-sm" onClick={() => navigate(`/envoyer?to=${username}`)} data-testid="respond-with-photo-button">
                          <ImagePlus className="h-4 w-4 mr-1" /> Répondre avec une photo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            // photo
            const mine = it.sender_id === user?.id;
            return (
              <div key={it.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="w-48 max-w-[75%]">
                  <LockedPhotoCard photo={it} onUnlock={mine ? null : openUnlock} footer={false} />
                  <p className="text-[11px] text-muted-foreground mt-1 px-1">
                    {mine ? (it.purchased ? "Achetée par le destinataire" : "En attente d'achat") : (it.purchased ? "Débloquée" : "Verrouillée")}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="flex items-center gap-2 pt-3 border-t border-border">
        <Button variant="outline" size="icon" className="rounded-xl shrink-0" onClick={() => navigate(`/envoyer?to=${username}`)} data-testid="conversation-send-photo-button" aria-label="Envoyer une photo">
          <ImagePlus className="h-5 w-5" />
        </Button>
        <Button variant="outline" size="icon" className="rounded-xl shrink-0" onClick={() => setReqOpen(true)} data-testid="conversation-request-photo-button" aria-label="Demander une photo">
          <HelpCircle className="h-5 w-5" />
        </Button>
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder={t("conv.write")} className="rounded-xl" data-testid="conversation-message-input" />
        <Button size="icon" className="rounded-xl shrink-0" onClick={sendMsg} data-testid="conversation-message-send" aria-label="Envoyer">
          <Send className="h-5 w-5" />
        </Button>
      </div>

      {/* Photo request dialog */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><HelpCircle className="h-5 w-5 text-primary" /> Demander une photo</DialogTitle>
            <DialogDescription>@{friend?.username || username} recevra votre demande et pourra vous envoyer une photo verrouillée.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={reqNote}
            onChange={(e) => setReqNote(e.target.value)}
            placeholder="Précisez ce que vous aimeriez (facultatif)…"
            className="rounded-xl"
            data-testid="photo-request-note"
            maxLength={280}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReqOpen(false)}>Annuler</Button>
            <Button onClick={sendRequest} className="neon-glow-blue-sm" data-testid="photo-request-send">Envoyer la demande</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription tiers dialog */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Star className="h-5 w-5 text-primary" /> S'abonner à @{creator?.creator?.username || username}</DialogTitle>
            <DialogDescription>2 mois d'essai gratuit, puis renouvellement automatique. Accès à toutes ses photos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(creator?.tiers || []).map((t) => (
              <button
                key={t.lookup_key}
                onClick={() => subscribe(t.lookup_key)}
                data-testid={`sub-tier-${t.lookup_key}`}
                className="w-full flex items-center justify-between rounded-xl border border-border hover:border-primary bg-card px-4 py-3 transition-colors"
              >
                <span className="font-medium">{t.label}</span>
                <span className="text-primary font-semibold">{euro(t.price)}</span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground text-center">Paiement sécurisé par carte via Stripe · essai gratuit 60 jours</p>
        </DialogContent>
      </Dialog>

      <UnlockPaymentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        photo={selected}
        balance={user?.wallet_balance || 0}
        onConfirm={confirmUnlock}
        onRecharge={() => navigate("/portefeuille")}
      />
    </div>
  );
};

export default Conversation;
